import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { APIProvider } from "@vis.gl/react-google-maps";
import type { Address, SimpleLatLng } from "@mysnowscape/snow-lib";

import PlaceAutocomplete from "../../pages/Admin/Jobs/PlaceAutocomplete";
import SubtleButton from "../Buttons/SubtleButton";
import LabeledTextboxInput from "../Inputs/LabeledTextboxInput";
import flash from "../../services/flash";
import { API_KEY } from "../../config/maps";

export type CompanyLocationFormValues = {
  name: string;
  address: string;
  location: SimpleLatLng;
};

type CompanyLocationFormProps = {
  mode?: "create" | "edit";
  initialName?: string;
  initialAddress?: string;
  initialLocation?: SimpleLatLng | null;
  saving?: boolean;
  compact?: boolean;
  onSave: (values: CompanyLocationFormValues) => void | Promise<void>;
  onCancel?: () => void;
  /** Override primary button label (defaults from mode). */
  saveLabel?: string;
  title?: string;
};

/** Shared add/edit company location form (Company Settings + Route Builder depot). */
const CompanyLocationForm: FC<CompanyLocationFormProps> = ({
  mode = "create",
  initialName = "",
  initialAddress = "",
  initialLocation = null,
  saving = false,
  compact = false,
  onSave,
  onCancel,
  saveLabel,
  title,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [address, setAddress] = useState(initialAddress);
  const [latLng, setLatLng] = useState<SimpleLatLng | null>(initialLocation);

  useEffect(() => {
    setName(initialName);
    setAddress(initialAddress);
    setLatLng(initialLocation);
  }, [initialName, initialAddress, initialLocation]);

  const handlePlaceSelect = (
    place: google.maps.places.PlaceResult,
    _address: Address,
    location: SimpleLatLng | null,
  ): void => {
    setAddress(place.formatted_address || "");
    setLatLng(location);
  };

  const handleSave = (): void => {
    if (!name.trim()) {
      flash.error(t("companyLocationForm.nameRequired"));
      return;
    }
    if (!latLng) {
      flash.error(t("companyLocationForm.addressRequired"));
      return;
    }
    void onSave({
      name: name.trim(),
      address,
      location: latLng,
    });
  };

  const heading =
    title ??
    (mode === "edit"
      ? t("companyLocationForm.editTitle")
      : t("companyLocationForm.addTitle"));
  const primaryLabel =
    saveLabel ??
    (mode === "edit" ? t("companyLocationForm.update") : t("common.save"));

  return (
    <div
      className={`w-full rounded-lg border border-blue-200 bg-blue-50 ${compact ? "p-2.5" : "p-4"}`}
      data-testid="company-location-form"
    >
      <div className={compact ? "mb-2.5 space-y-2" : "mb-4"}>
        {!compact ? (
          <p className="mb-2 text-sm font-semibold text-slate-700">{heading}</p>
        ) : null}

        <APIProvider apiKey={API_KEY}>
          <div className={compact ? "" : "mb-3"}>
            <label className="mb-1 block text-xs text-gray-600">
              {t("companyLocationForm.address")}
            </label>
            <PlaceAutocomplete
              value={address}
              onPlaceSelect={handlePlaceSelect}
              placeholder={t("companyLocationForm.addressPlaceholder")}
              autoFocus={true}
              className={
                compact
                  ? "w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-snow-blue-3"
                  : undefined
              }
            />
          </div>
        </APIProvider>

        {latLng && !compact ? (
          <p className="mb-3 text-xs text-gray-600">
            {t("companyLocationForm.coordinates", {
              lat: latLng.lat?.toFixed(4),
              lng: latLng.lng?.toFixed(4),
            })}
          </p>
        ) : null}

        {compact ? (
          <label className="block text-xs text-gray-600">
            {t("companyLocationForm.compactName")}
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 outline-none focus:border-snow-blue-3"
              placeholder={t("companyLocationForm.compactNamePlaceholder")}
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={50}
              disabled={saving}
              data-testid="input-Location Name"
            />
          </label>
        ) : (
          <LabeledTextboxInput
            label={t("companyLocationForm.name")}
            placeholder={t("companyLocationForm.namePlaceholder")}
            value={name}
            setValue={setName}
            maxLength={50}
          />
        )}
      </div>

      <div className="flex gap-2">
        <SubtleButton
          buttonRole="primary"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
          className={compact ? "!px-2.5 !py-1.5" : undefined}
          testId="company-location-form-save"
        >
          {primaryLabel}
        </SubtleButton>
        {onCancel ? (
          <SubtleButton
            onClick={onCancel}
            disabled={saving}
            className={compact ? "!px-2.5 !py-1.5" : undefined}
            testId="company-location-form-cancel"
          >
            {t("common.cancel")}
          </SubtleButton>
        ) : null}
      </div>
    </div>
  );
};

export default CompanyLocationForm;
